import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { CustomEventsTableBody } from 'modules/customEvents/components/CustomEventsTableBody';
import { CustomEventsTableHeaderRow } from 'modules/customEvents/components/CustomEventsTableHeaderRow';
import { CustomEventsTableRow } from 'modules/customEvents/components/CustomEventsTableRow';
import { ReferencesDrawer } from 'modules/customEvents/components/ReferencesDrawer';
import { useSelectionContext } from 'modules/customEvents/contexts';
import { useCustomEventsTableEmptyState } from 'modules/customEvents/hooks';
import { INGESTION_POLICIES } from 'modules/customEvents/types';
import { groupSelectStatus } from 'modules/customEvents/utils';
import { DATA_SCHEMA_MANAGEMENT_URL } from 'modules/dataDictionary/constants';
import { useSearchParamsContext } from 'modules/dataDictionary/contexts';
import { useCustomEventsTable } from 'modules/dataDictionary/hooks';
import { useEffect } from 'react';
import { TabsGroup } from 'ui/tabsGroup';
import { useSortingContext } from 'utils/contexts/sorting';
import { Table, TablePagination } from '../Table';
import { CustomEventsActions } from './CustomEventsActions';

const { ASCENDING, DESCENDING } = SORT_DIRECTIONS;
const baseUrl = `${DATA_SCHEMA_MANAGEMENT_URL}/customEvent/`;

export const CustomEventsContent = () => {
  const { selected, selectByIds, toggleSelection, deselectAll } = useSelectionContext();
  const { setSortBy, setSortDirection, sortBy, sortDirection } = useSortingContext();
  const { search, ingestionPolicy, resetSearchParams, setIngestionPolicy } =
    useSearchParamsContext();

  const { isLoading, filteredResults, paginatedSortedEvents } = useCustomEventsTable();

  const emptyStateConfig = useCustomEventsTableEmptyState({
    ingestionPolicyFilter: ingestionPolicy as INGESTION_POLICIES,
    searchQuery: search,
    startDate: '',
    endDate: '',
    preset: '',
  });

  const handleSort = (name: string) => {
    if (name === sortBy) {
      setSortDirection(sortDirection === ASCENDING ? DESCENDING : ASCENDING);
    } else {
      setSortBy(name);
      setSortDirection(ASCENDING);
    }
  };

  const onSelectAll = () => {
    // only select items on current page due to pagination
    const eventIds = paginatedSortedEvents.map((event) => event.eventName);
    selectByIds(eventIds);
  };
  const checkboxStatus = groupSelectStatus(selected.length, paginatedSortedEvents.length);

  /**
   * Reset search params when component unmounts
   */
  useEffect(() => {
    return () => {
      resetSearchParams();
    };
  }, []);

  return (
    <>
      <TabsGroup
        data-test="custom-events-state-tabs-group"
        defaultValue={ingestionPolicy}
        onValueChange={setIngestionPolicy}
        value={ingestionPolicy}
      >
        <CustomEventsActions filteredCustomEvents={paginatedSortedEvents} />
      </TabsGroup>
      <Table>
        <CustomEventsTableHeaderRow
          checkboxStatus={checkboxStatus}
          deselectAll={deselectAll}
          onSelectAll={onSelectAll}
          sortBy={sortBy}
          sortDirection={sortDirection}
          updateSort={handleSort}
        />
        <CustomEventsTableBody
          emptyStateConfig={emptyStateConfig}
          isLoading={isLoading}
          length={paginatedSortedEvents?.length}
        >
          {paginatedSortedEvents.map((event) => {
            const encodedName = encodeURIComponent(event.eventName);
            return (
              <CustomEventsTableRow
                key={event.eventName}
                customEvent={event}
                href={`${baseUrl}${encodedName}`}
                ingestionPolicyFilter={ingestionPolicy as INGESTION_POLICIES}
                toggleSelection={toggleSelection}
                selected={selected}
              />
            );
          })}
        </CustomEventsTableBody>
      </Table>
      <TablePagination numItems={filteredResults.length} />
      <ReferencesDrawer />
    </>
  );
};
